import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './LandingPage.css';

const LandingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('login');
  const navigate = useNavigate();

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  const handleRegistrationComplete = () => {
    navigate('/registration-complete');
  };

  return (
    <div className="landing-container">
      <div className="landing-background">
        <div className="hero-section">
          <div className="brand-header">
            <div className="logo-container">
              <div className="logo">
                <span className="logo-icon">🏆</span>
                <span className="logo-text">Sportagram</span>
              </div>
            </div>
            <p className="tagline">Where Passion Meets Performance</p>
          </div>

          <div className="auth-card">
            <div className="tab-navigation">
              <button 
                className={`tab-button ${activeTab === 'login' ? 'active' : ''}`}
                onClick={() => handleTabChange('login')}
              >
                Sign In
              </button>
              <button 
                className={`tab-button ${activeTab === 'register' ? 'active' : ''}`}
                onClick={() => handleTabChange('register')}
              >
                Sign Up
              </button>
            </div>

            <div className="tab-content">
              {activeTab === 'login' && (
                <div className="login-form">
                  <h2>Welcome Back!</h2>
                  <p className="form-subtitle">Sign in to continue your athletic journey</p>
                  
                  <form className="auth-form">
                    <div className="form-group">
                      <label htmlFor="email">Email Address</label>
                      <input 
                        type="email" 
                        id="email" 
                        placeholder="Enter your email"
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="password">Password</label>
                      <input 
                        type="password" 
                        id="password" 
                        placeholder="Enter your password"
                        className="form-input"
                      />
                    </div>

                    <div className="form-options">
                      <label className="checkbox-container">
                        <input type="checkbox" />
                        <span className="checkmark"></span>
                        Remember me
                      </label>
                      <button 
                        type="button" 
                        className="forgot-link"
                        onClick={handleForgotPassword}
                      >
                        Forgot Password?
                      </button>
                    </div>

                    <button type="submit" className="auth-button primary">
                      Sign In
                    </button>
                  </form>

                  <div className="social-login">
                    <div className="divider">
                      <span>or continue with</span>
                    </div>
                    <div className="social-buttons">
                      <button className="social-button google">
                        <span className="social-icon">🔍</span>
                        Google
                      </button>
                      <button className="social-button apple">
                        <span className="social-icon">🍎</span>
                        Apple
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'register' && (
                <div className="register-form">
                  <h2>Join Sportagram</h2>
                  <p className="form-subtitle">Start your journey to athletic excellence</p>
                  
                  <form className="auth-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="firstName">First Name</label>
                        <input 
                          type="text" 
                          id="firstName" 
                          placeholder="First name"
                          className="form-input"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="lastName">Last Name</label>
                        <input 
                          type="text" 
                          id="lastName" 
                          placeholder="Last name"
                          className="form-input"
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="registerEmail">Email Address</label>
                      <input 
                        type="email" 
                        id="registerEmail" 
                        placeholder="Enter your email"
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="registerPassword">Password</label>
                      <input 
                        type="password" 
                        id="registerPassword" 
                        placeholder="Create a strong password"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="confirmPassword">Confirm Password</label>
                      <input 
                        type="password" 
                        id="confirmPassword" 
                        placeholder="Confirm your password"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="sport">Primary Sport</label>
                      <select id="sport" className="form-input">
                        <option value="">Select your sport</option>
                        <option value="football">Football</option>
                        <option value="basketball">Basketball</option>
                        <option value="tennis">Tennis</option>
                        <option value="swimming">Swimming</option>
                        <option value="running">Running</option>
                        <option value="cycling">Cycling</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div className="form-options">
                      <label className="checkbox-container">
                        <input type="checkbox" required />
                        <span className="checkmark"></span>
                        I agree to the <Link to="/terms-of-use" className="link">Terms of Service</Link> and <Link to="/privacy-policy" className="link">Privacy Policy</Link>
                      </label>
                    </div>

                    <button 
                      type="submit" 
                      className="auth-button primary"
                      onClick={handleRegistrationComplete}
                    >
                      Create Account
                    </button>
                  </form>

                  <div className="social-login">
                    <div className="divider">
                      <span>or sign up with</span>
                    </div>
                    <div className="social-buttons">
                      <button className="social-button google">
                        <span className="social-icon">🔍</span>
                        Google
                      </button>
                      <button className="social-button apple">
                        <span className="social-icon">🍎</span>
                        Apple
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="features-section">
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>Track Performance</h3>
              <p>Monitor your progress with detailed analytics and insights</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🤝</div>
              <h3>Connect & Compete</h3>
              <p>Join a community of athletes and challenge yourself</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎯</div>
              <h3>Achieve Goals</h3>
              <p>Set targets and celebrate your athletic achievements</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
